export const WORKSPACE_PREF_NAMESPACE = 'viewer.workspace_chrome';
export const WORKSPACE_PREF_STORAGE_KEY = 'kairos.viewerWorkspaceChrome';

export const TOOL_COMPLETION_BEHAVIORS = Object.freeze({
  SCROLL: 'scroll',
  STAY: 'stay',
});

export const WORKSPACE_PRESET_IDS = Object.freeze({
  DIAGNOSTIC: 'diagnostic',
  REPORTING: 'reporting',
  PIXEL_FOCUS: 'pixel-focus',
  CUSTOM: 'custom',
});

export const WORKSPACE_PRESETS = Object.freeze([
  {
    id: WORKSPACE_PRESET_IDS.DIAGNOSTIC,
    label: 'DIAG',
    description: 'Diagnostic read: Series and Report rails visible, no pixel-focus override.',
    prefs: { pixelFocus: false, seriesRailVisible: true, reportRailVisible: true },
  },
  {
    id: WORKSPACE_PRESET_IDS.REPORTING,
    label: 'REPORT',
    description: 'Reporting: keep the report rail visible and tuck the Series rail away from the dictation lane.',
    prefs: { pixelFocus: false, seriesRailVisible: false, reportRailVisible: true },
  },
  {
    id: WORKSPACE_PRESET_IDS.PIXEL_FOCUS,
    label: 'FOCUS',
    description: 'Pixel focus: temporarily hide rails at render time while preserving stored rail preferences.',
    prefs: { pixelFocus: true, seriesRailVisible: true, reportRailVisible: true },
  },
]);

const KNOWN_WORKSPACE_PRESET_IDS = new Set([
  WORKSPACE_PRESET_IDS.DIAGNOSTIC,
  WORKSPACE_PRESET_IDS.REPORTING,
  WORKSPACE_PRESET_IDS.PIXEL_FOCUS,
  WORKSPACE_PRESET_IDS.CUSTOM,
]);

export const DEFAULT_WORKSPACE_PREFS = Object.freeze({
  workspacePreset: WORKSPACE_PRESET_IDS.DIAGNOSTIC,
  pixelFocus: false,
  seriesRailVisible: true,
  seriesRailPosition: 'left',
  reportRailVisible: true,
  commandDeckCollapsed: false,
  toolCompletionBehavior: TOOL_COMPLETION_BEHAVIORS.SCROLL,
  escapeReturnsToScroll: true,
});

const safeJson = (raw) => {
  try { return raw ? JSON.parse(raw) : null; } catch (_) { return null; }
};

export const normalizeWorkspacePrefs = (value = {}) => {
  const source = value && typeof value === 'object' ? value : {};
  const workspacePreset = KNOWN_WORKSPACE_PRESET_IDS.has(source.workspacePreset)
    ? source.workspacePreset
    : WORKSPACE_PRESET_IDS.DIAGNOSTIC;
  return {
    workspacePreset,
    pixelFocus: Boolean(source.pixelFocus),
    seriesRailVisible: source.seriesRailVisible !== false,
    seriesRailPosition: ['left', 'top', 'bottom'].includes(source.seriesRailPosition) ? source.seriesRailPosition : 'left',
    reportRailVisible: source.reportRailVisible !== false,
    commandDeckCollapsed: Boolean(source.commandDeckCollapsed),
    toolCompletionBehavior: source.toolCompletionBehavior === TOOL_COMPLETION_BEHAVIORS.STAY ? TOOL_COMPLETION_BEHAVIORS.STAY : TOOL_COMPLETION_BEHAVIORS.SCROLL,
    escapeReturnsToScroll: source.escapeReturnsToScroll !== false,
  };
};

export const applyWorkspacePreset = (presetId, current = {}) => {
  const normalized = normalizeWorkspacePrefs(current);
  const preset = WORKSPACE_PRESETS.find(item => item.id === presetId);
  if (!preset) return { ...normalized, workspacePreset: WORKSPACE_PRESET_IDS.CUSTOM };
  return normalizeWorkspacePrefs({ ...normalized, ...preset.prefs, workspacePreset: preset.id });
};

export const getEffectiveWorkspaceChromeVisibility = (value = {}) => {
  const normalized = normalizeWorkspacePrefs(value);
  return {
    seriesRailVisible: normalized.seriesRailVisible && !normalized.pixelFocus,
    reportRailVisible: normalized.reportRailVisible && !normalized.pixelFocus,
  };
};

export const loadWorkspacePrefs = () => {
  if (typeof window === 'undefined') return DEFAULT_WORKSPACE_PREFS;
  return normalizeWorkspacePrefs(safeJson(window.localStorage.getItem(WORKSPACE_PREF_STORAGE_KEY)));
};

export const saveWorkspacePrefs = (value) => {
  const normalized = normalizeWorkspacePrefs(value);
  if (typeof window !== 'undefined') {
    try {
      window.localStorage.setItem(WORKSPACE_PREF_STORAGE_KEY, JSON.stringify(normalized));
    } catch (_) {
      // Profile preference save is authoritative; localStorage is only a workstation fallback.
    }
  }
  return normalized;
};
